import React from 'react';
import { Check, RotateCcw, Hand } from 'lucide-react';
import { computeNextMarkers } from '../../domains/process/shapes/bpmn/bpmnActivityMarkerRules';
import { BaseTool, IToolComponentProps, IToolContext, IToolManifest } from '../../core/tools/interfaces/ITool';
import { CompositeCommand, ICommand } from '../../core/commands/ICommand';
import type { Node } from '../../core/models/scene';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { UpdateFlowchartNodeCommand } from '../../domains/process/commands/UpdateFlowchartNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { cn } from '../../lib/utils';
import { useTranslation } from '../../hooks/useTranslation';

type ActivityType = 'task' | 'transaction' | 'event-sub-process' | 'call-activity';
type ActivityMarkerType =
  | 'none'
  | 'sub-process'
  | 'loop'
  | 'multi-instance-parallel'
  | 'multi-instance-sequential'
  | 'ad-hoc'
  | 'compensation'
  | 'reset';
type ActivityTaskType = 'none' | 'send' | 'receive' | 'user' | 'manual' | 'business-rule' | 'service' | 'script';

type ActivityProps = Record<string, unknown> & {
  attrs?: Record<string, unknown>;
  activityType?: ActivityType;
  activityMarker?: ActivityMarkerType;
  activityMarkers?: ActivityMarkerType[];
  loopType?: ActivityMarkerType;
  taskType?: ActivityTaskType;
};

type BpmnActivityNode = Node & {
  activityType?: ActivityType;
  activityMarker?: ActivityMarkerType;
  activityMarkers?: ActivityMarkerType[];
  loopType?: ActivityMarkerType;
  taskType?: ActivityTaskType;
  props?: ActivityProps;
};

type BpmnActivityPatch = Partial<Node> & {
  activityType?: string;
  activityMarker?: string;
  activityMarkers?: ActivityMarkerType[];
  taskType?: string;
};

type SelectedShapeWithData = {
  data?: BpmnActivityNode;
};

const normalizeVariant = (node?: { attrs?: Record<string, unknown>; name?: unknown; type?: unknown } | null): string => {
  const attrs = node?.attrs || {};
  const raw = attrs.variant || attrs.shapeType || node?.name || node?.type || '';
  return String(raw).toLowerCase().replace(/\s+/g, '-');
};

const isBpmnActivityNode = (node?: Node | null): node is BpmnActivityNode => normalizeVariant(node) === 'bpmn-activity';

const getSelectedActivityNodes = (context: IToolContext): BpmnActivityNode[] => {
  if (typeof context.getSelectedShapes !== 'function') return [];
  return context
    .getSelectedShapes()
    .map((shape) => (shape as SelectedShapeWithData).data)
    .filter((node): node is BpmnActivityNode => !!node && isBpmnActivityNode(node));
};

const ActivityTypeIcon: React.FC<{ type: ActivityType; className?: string }> = ({ type, className }) => {
  if (type === 'transaction') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="3" y="5.5" width="18" height="13" rx="3" stroke="currentColor" strokeWidth="1.6" />
        <rect x="5.2" y="7.7" width="13.6" height="8.6" rx="2" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    );
  }
  if (type === 'event-sub-process') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="3.6" y="6" width="16.8" height="12" rx="3" stroke="currentColor" strokeWidth="1.8" strokeDasharray="2.6 2.2" />
      </svg>
    );
  }
  if (type === 'call-activity') {
    return (
      <svg viewBox="0 0 24 24" fill="none" className={className}>
        <rect x="3.3" y="5.5" width="17.4" height="13" rx="3" stroke="currentColor" strokeWidth="2.4" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className={className}>
      <rect x="3.6" y="6" width="16.8" height="12" rx="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
};

const MarkerNoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="4.8" y="7.5" width="14.4" height="9" rx="2" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.4 2" />
  </svg>
);
const MarkerSubProcessIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="5.5" y="6.3" width="13" height="11.4" stroke="currentColor" strokeWidth="1.7" />
    <path d="M12 8.7V15.3M8.7 12H15.3" stroke="currentColor" strokeWidth="1.9" />
  </svg>
);
const MarkerLoopIcon: React.FC<{ className?: string }> = ({ className }) => <RotateCcw className={className} />;
const MarkerParallelMiIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M8 6V18M12 6V18M16 6V18" stroke="currentColor" strokeWidth="2.2" />
  </svg>
);
const MarkerSequentialMiIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M6.5 8H17.5M6.5 12H17.5M6.5 16H17.5" stroke="currentColor" strokeWidth="2.2" />
  </svg>
);
const MarkerAdHocIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M6.9 13.2C9.3 8.1 12.6 15.9 16.8 11.4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const MarkerCompensationIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M11.8 7.2L6.4 12L11.8 16.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M17.6 7.2L12.2 12L17.6 16.8Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
  </svg>
);

const TaskNoneIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="5.1" y="7.4" width="13.8" height="9.2" rx="1.8" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2.4 2" />
  </svg>
);
const TaskSendIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none">
    <rect x="5" y="7" width="14" height="10" fill="currentColor" />
    <path d="M5 7L12 12.5L19 7" stroke="#FFFFFF" strokeWidth="1.6" />
  </svg>
);
const TaskReceiveIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="5" y="7" width="14" height="10" stroke="currentColor" strokeWidth="1.6" />
    <path d="M5 7L12 12.5L19 7" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);
const TaskUserIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <circle cx="12" cy="8" r="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M20 21a8 8 0 0 0-16 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const TaskManualIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Hand className={cn(className, "rotate-90")} style={{ strokeWidth: 1 }} />
);
const TaskBusinessRuleIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <rect x="5.2" y="6.8" width="13.6" height="10.4" stroke="currentColor" strokeWidth="1.35" />
    <path d="M5.2 10.2H18.8M9.5 10.2V17.2" stroke="currentColor" strokeWidth="1.35" />
  </svg>
);
const TaskServiceIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} style={{ transform: 'scale(1.4)' }}>
    <circle cx="10.2" cy="13.1" r="2.6" stroke="currentColor" strokeWidth="1.2" />
    <path d="M10.2 9V10.1M10.2 16.1V17.2M6.1 13.1H7.2M13.2 13.1H14.3M7.3 10.3L8.1 11.1M12.3 15.3L13.1 16.1M7.3 15.9L8.1 15.1M12.3 10.9L13.1 10.1" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="14.2" cy="9.2" r="2" stroke="currentColor" strokeWidth="1.1" />
    <path d="M14.2 6.3V7.1M14.2 11.3V12.1M11.3 9.2H12.1M16.3 9.2H17.1M12.3 7.3L12.9 7.9M15.5 10.5L16.1 11.1M12.3 11.1L12.9 10.5M15.5 7.9L16.1 7.3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);
const TaskScriptIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path d="M15 12h-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 8h-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M19 17V5a2 2 0 0 0-2-2H4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 21h12a2 2 0 0 0 2-2v-1a1 1 0 0 0-1-1H11a1 1 0 0 0-1 1v1a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v2a1 1 0 0 0 1 1h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ACTIVITY_STYLE_OPTIONS: Array<{ value: ActivityType; labelKey: string; Icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'task', labelKey: 'tools.bpmnActivity.styles.task', Icon: ({ className }) => <ActivityTypeIcon type="task" className={className} /> },
  { value: 'transaction', labelKey: 'tools.bpmnActivity.styles.transaction', Icon: ({ className }) => <ActivityTypeIcon type="transaction" className={className} /> },
  { value: 'event-sub-process', labelKey: 'tools.bpmnActivity.styles.event-sub-process', Icon: ({ className }) => <ActivityTypeIcon type="event-sub-process" className={className} /> },
  { value: 'call-activity', labelKey: 'tools.bpmnActivity.styles.call-activity', Icon: ({ className }) => <ActivityTypeIcon type="call-activity" className={className} /> },
];

const ACTIVITY_MARKER_OPTIONS: Array<{ value: ActivityMarkerType; labelKey: string; Icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'none', labelKey: 'tools.bpmnActivity.markers.none', Icon: MarkerNoneIcon },
  { value: 'sub-process', labelKey: 'tools.bpmnActivity.markers.sub-process', Icon: MarkerSubProcessIcon },
  { value: 'loop', labelKey: 'tools.bpmnActivity.markers.loop', Icon: MarkerLoopIcon },
  { value: 'multi-instance-parallel', labelKey: 'tools.bpmnActivity.markers.multi-instance-parallel', Icon: MarkerParallelMiIcon },
  { value: 'multi-instance-sequential', labelKey: 'tools.bpmnActivity.markers.multi-instance-sequential', Icon: MarkerSequentialMiIcon },
  { value: 'ad-hoc', labelKey: 'tools.bpmnActivity.markers.ad-hoc', Icon: MarkerAdHocIcon },
  { value: 'compensation', labelKey: 'tools.bpmnActivity.markers.compensation', Icon: MarkerCompensationIcon },
];

const ACTIVITY_TASK_OPTIONS: Array<{ value: ActivityTaskType; labelKey: string; Icon: React.ComponentType<{ className?: string }> }> = [
  { value: 'none', labelKey: 'tools.bpmnActivity.tasks.none', Icon: TaskNoneIcon },
  { value: 'send', labelKey: 'tools.bpmnActivity.tasks.send', Icon: TaskSendIcon },
  { value: 'receive', labelKey: 'tools.bpmnActivity.tasks.receive', Icon: TaskReceiveIcon },
  { value: 'user', labelKey: 'tools.bpmnActivity.tasks.user', Icon: TaskUserIcon },
  { value: 'manual', labelKey: 'tools.bpmnActivity.tasks.manual', Icon: TaskManualIcon },
  { value: 'business-rule', labelKey: 'tools.bpmnActivity.tasks.business-rule', Icon: TaskBusinessRuleIcon },
  { value: 'service', labelKey: 'tools.bpmnActivity.tasks.service', Icon: TaskServiceIcon },
  { value: 'script', labelKey: 'tools.bpmnActivity.tasks.script', Icon: TaskScriptIcon },
];

export const getUniformValue = <TNode, T extends string>(nodes: TNode[], reader: (node: TNode) => T, fallback: T): T => {
  if (nodes.length === 0) return fallback;
  const first = reader(nodes[0]);
  return nodes.every((node) => reader(node) === first) ? first : fallback;
};

const nodeRecord = (node: BpmnActivityNode): Record<string, unknown> => node as unknown as Record<string, unknown>;

const propsAttrs = (node: BpmnActivityNode): Record<string, unknown> => node.props?.attrs || {};

const readNodeValue = <T extends string>(
  node: BpmnActivityNode,
  key: 'activityType' | 'activityMarker' | 'loopType' | 'taskType',
  fallback: T,
): T => {
  const direct = nodeRecord(node);
  return String(node.attrs?.[key] ?? propsAttrs(node)[key] ?? node.props?.[key] ?? direct[key] ?? fallback) as T;
};

const buildPatchCommand = (nodeId: string, node: BpmnActivityNode, patch: BpmnActivityPatch) => {
  const currentAttrs = node.attrs || {};
  const currentProps = node.props || {};
  const currentPropsAttrs = currentProps.attrs || {};
  const mergedAttrs = { ...currentPropsAttrs, ...currentAttrs, ...patch };
  return new UpdateFlowchartNodeCommand({
    nodeId,
    changes: {
      ...patch,
      attrs: mergedAttrs,
      props: { ...currentProps, ...patch, attrs: mergedAttrs },
    },
  });
};

const createActivityTool = (
  manifest: IToolManifest,
  getCurrentValue: (nodes: BpmnActivityNode[]) => string,
  options: Array<{ value: string; labelKey: string; Icon: React.ComponentType<{ className?: string }> }>,
  executePatch: (value: string) => BpmnActivityPatch,
) => {
  const Component: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
    const { t } = useTranslation('diagram');
    const [open, setOpen] = React.useState(false);
    const selected = React.useMemo(() => getSelectedActivityNodes(context), [context, context.selection]);
    const current = React.useMemo(() => getCurrentValue(selected), [selected]);
    const ActiveIcon = options.find((o) => o.value === current)?.Icon ?? options[0].Icon;
    const isTaskDropdown = manifest.id === 'bpmn-activity-task';
    const toolTestId = manifest.id === 'bpmn-activity-task' ? 'task' : manifest.id.replace('bpmn-activity-', '');

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`tid.tools.bpmnActivity.${toolTestId}.trigger`}
            className="w-8 h-8 flex items-center justify-center hover:bg-muted rounded transition-colors"
            aria-label={manifest.name}
          >
            <ActiveIcon className="h-4 w-4 text-foreground" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-2" align="start">
          <div className={`space-y-1 ${isTaskDropdown ? 'max-h-[360px] overflow-y-auto pr-1' : ''}`}>
            {options.map((option) => {
              const Icon = option.Icon;
              const isActive = option.value === current;
              return (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`tid.tools.bpmnActivity.${toolTestId}.${option.value}`}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-muted transition-colors text-left"
                  onClick={() => {
                    onExecute(executePatch(option.value));
                    setOpen(false);
                  }}
                >
                  <Icon className="h-4 w-4 text-foreground" />
                  <span className="text-[14px] text-foreground">{t(option.labelKey)}</span>
                  {isActive && <Check className="ml-auto h-4 w-4 text-foreground" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return class extends BaseTool {
    manifest = manifest;

    canExecute(context: IToolContext): boolean {
      if (!super.canExecute(context) || context.diagramType !== 'flowchart' || context.selection.size === 0) return false;
      const ids = Array.from(context.selection);
      const nodes = useDiagramStore.getState().nodes.filter((n) => ids.includes(n.id));
      return nodes.length > 0 && nodes.every((n) => isBpmnActivityNode(n));
    }

    execute(params?: BpmnActivityPatch): ICommand | null {
      if (!this.context || this.context.selection.size === 0 || !params) return null;
      const ids = Array.from(this.context.selection);
      const byId = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));
      const commands = ids
        .map((id) => {
          const node = byId.get(id);
          if (!node || !isBpmnActivityNode(node)) return null;
          return buildPatchCommand(id, node, params);
        })
        .filter(Boolean) as ICommand[];
      if (commands.length === 0) return null;
      if (commands.length === 1) return commands[0];
      return new CompositeCommand(`Update ${manifest.name}`, commands);
    }

    getToolbarComponent() { return Component; }
    getFloatingPanelComponent() { return Component; }
  };
};

const BpmnActivityStyleBase = createActivityTool(
  {
    id: 'bpmn-activity-style',
    name: 'tools.bpmnActivity.style',
    icon: 'square',
    category: 'style',
    description: 'Change BPMN activity style',
    version: '1.0.0',
    applicability: { diagramTypes: ['flowchart'], contextTypes: ['genericShape'] },
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: true, contextMenu: false, propertyPanel: false, sidebar: false, floatingPanel: true, statusBar: false },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  },
  (nodes) =>
    getUniformValue(
      nodes,
      (n) =>
        readNodeValue<ActivityType>(n, 'activityType', 'task'),
      'task'
    ),
  ACTIVITY_STYLE_OPTIONS,
  (value) => ({ activityType: value }),
);

const getMarkerValues = (node: BpmnActivityNode): ActivityMarkerType[] => {
  const direct = nodeRecord(node);
  const arr = node.attrs?.activityMarkers ?? propsAttrs(node).activityMarkers ?? node.props?.activityMarkers ?? direct.activityMarkers;
  if (Array.isArray(arr) && arr.length >= 1) {
    return arr.slice(0, 3).filter((v): v is ActivityMarkerType => typeof v === 'string' && v !== 'none') as ActivityMarkerType[];
  }
  const single =
    (node.attrs?.activityMarker ??
      propsAttrs(node).activityMarker ??
      node.props?.activityMarker ??
      direct.activityMarker ??
      node.attrs?.loopType ??
      propsAttrs(node).loopType ??
      node.props?.loopType ??
      direct.loopType ??
      'none') as ActivityMarkerType;
  return single && single !== 'none' ? [single] : [];
};

const getUniformMarkers = (nodes: BpmnActivityNode[]): ActivityMarkerType[] => {
  if (nodes.length === 0) return [];
  const first = getMarkerValues(nodes[0]);
  const same = nodes.every((n) => {
    const v = getMarkerValues(n);
    return v.length === first.length && v.every((m, i) => m === first[i]);
  });
  return same ? first : first;
};

const BpmnActivityMarkerBase = (() => {
  const manifest: IToolManifest = {
    id: 'bpmn-activity-marker',
    name: 'tools.bpmnActivity.marker',
    icon: 'minus',
    category: 'style',
    description: 'Add or remove activity markers (up to 3 with compatibility rules)',
    version: '1.0.0',
    applicability: { diagramTypes: ['flowchart'], contextTypes: ['genericShape'] },
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: true, contextMenu: false, propertyPanel: false, sidebar: false, floatingPanel: true, statusBar: false },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  const Component: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
    const { t } = useTranslation('diagram');
    const [open, setOpen] = React.useState(false);
    const selected = React.useMemo(() => getSelectedActivityNodes(context), [context, context.selection]);
    const current = React.useMemo(() => getUniformMarkers(selected), [selected]);

    const toggleMarker = (value: ActivityMarkerType) => {
      if (value === 'none') {
        onExecute({ activityMarker: 'none', activityMarkers: [] });
        setOpen(false);
        return;
      }
      const next = computeNextMarkers(current, value);
      const patch: Record<string, unknown> =
        next.length === 0
          ? { activityMarker: 'none', activityMarkers: [] }
          : { activityMarker: next[0], activityMarkers: next };
      onExecute(patch);
      setOpen(false);
    };

    const iconCount = current.length;
    const isMulti = iconCount >= 2;
    const SingleIcon = ACTIVITY_MARKER_OPTIONS.find((o) => o.value === current[0])?.Icon ?? MarkerNoneIcon;
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="tid.tools.bpmnActivity.marker.trigger"
            className={cn(
              'h-8 flex items-center justify-center hover:bg-muted rounded transition-colors flex-shrink-0',
              isMulti ? 'min-w-10 w-10 px-0.5' : 'w-8',
            )}
            aria-label={manifest.name}
          >
            {iconCount === 0 ? (
              <MarkerNoneIcon className="h-4 w-4 text-foreground flex-shrink-0" />
            ) : iconCount === 1 ? (
              <SingleIcon className="h-4 w-4 text-foreground flex-shrink-0" />
            ) : (
              <span className="flex items-center justify-center gap-0.5 flex-shrink-0">
                {current.slice(0, 3).map((m, i) => {
                  const Icon = ACTIVITY_MARKER_OPTIONS.find((o) => o.value === m)?.Icon ?? MarkerNoneIcon;
                  return <Icon key={i} className="h-3 w-3 text-foreground flex-shrink-0" />;
                })}
              </span>
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[320px] p-2" align="start">
          <div className="space-y-1">
            {ACTIVITY_MARKER_OPTIONS.map((option) => {
              const Icon = option.Icon;
              const isActive = option.value === 'none' ? current.length === 0 : current.includes(option.value as ActivityMarkerType);
              return (
                <button
                  key={option.value}
                  type="button"
                  data-testid={`tid.tools.bpmnActivity.marker.${option.value}`}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded hover:bg-muted transition-colors text-left"
                  onClick={() => toggleMarker(option.value as ActivityMarkerType)}
                >
                  <Icon className="h-4 w-4 text-foreground" />
                  <span className="text-[14px] text-foreground">{t(option.labelKey)}</span>
                  {isActive && <Check className="ml-auto h-4 w-4 text-foreground" />}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
    );
  };

  return class extends BaseTool {
    manifest = manifest;

    canExecute(context: IToolContext): boolean {
      if (!super.canExecute(context) || context.diagramType !== 'flowchart' || context.selection.size === 0) return false;
      const ids = Array.from(context.selection);
      const nodes = useDiagramStore.getState().nodes.filter((n) => ids.includes(n.id));
      return nodes.length > 0 && nodes.every((n) => isBpmnActivityNode(n));
    }

    execute(params?: BpmnActivityPatch): ICommand | null {
      if (!this.context || this.context.selection.size === 0 || !params) return null;
      const ids = Array.from(this.context.selection);
      const byId = new Map(useDiagramStore.getState().nodes.map((n) => [n.id, n]));
      const commands = ids
        .map((id) => {
          const node = byId.get(id);
          if (!node || !isBpmnActivityNode(node)) return null;
          return buildPatchCommand(id, node, params);
        })
        .filter(Boolean) as ICommand[];
      if (commands.length === 0) return null;
      if (commands.length === 1) return commands[0];
      return new CompositeCommand(`Update ${manifest.name}`, commands);
    }

    getToolbarComponent() { return Component; }
    getFloatingPanelComponent() { return Component; }
  };
})();

const BpmnActivityTaskBase = createActivityTool(
  {
    id: 'bpmn-activity-task',
    name: 'tools.bpmnActivity.taskType',
    icon: 'mail',
    category: 'style',
    description: 'Change BPMN task icon type',
    version: '1.0.0',
    applicability: { diagramTypes: ['flowchart'], contextTypes: ['genericShape'] },
    requiresSelection: true,
    minSelection: 1,
    ui: { toolbar: true, contextMenu: false, propertyPanel: false, sidebar: false, floatingPanel: true, statusBar: false },
    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  },
  (nodes) =>
    getUniformValue(
      nodes,
      (n) =>
        readNodeValue<ActivityTaskType>(n, 'taskType', 'none'),
      'none'
    ),
  ACTIVITY_TASK_OPTIONS,
  (value) => ({ taskType: value }),
);

export class BpmnActivityStyleTool extends BpmnActivityStyleBase {}
export class BpmnActivityMarkerTool extends BpmnActivityMarkerBase {}
export class BpmnActivityTaskTool extends BpmnActivityTaskBase {}
